/* eslint-disable @typescript-eslint/member-ordering */
import {
  AfterViewInit,
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {NavigationEventService} from './add-ons/navigation-event.service';
import * as addonSelectors from './add-ons/reducers';
import * as contextActions from './common/context-data';
import {ComponentRenderMode} from './common/context-data';
import {
  AddOnErrorEvent,
  AuthenticateEvent,
  CartChangeEvent,
  ContinueEvent,
  NavigationEvent,
} from './common/event-emitter/event.interface';
import {SeFeThemesLoader} from 'se-fe-themes';
import {CommerceInitializationService} from './commerce-initialization.service';
import {CommerceNavigationEventEmitter} from './common/event-emitter/commerce-event-emitter';
import {emptyCart} from './common/shopping-cart/cart.actions';

@Component({
  selector: 'sales-channels-element',
  templateUrl: './commerce.component.html',
  styleUrls: ['./commerce.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  host: {class: 'sales-channels-element'},
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CommerceComponent
  implements OnInit, OnDestroy, AfterViewInit, CommerceNavigationEventEmitter
{
  /**
   * The iFrame that is used to go through the OIDC flow without the
   * user noticing.
   */
  @ViewChild('Oidc') silentOidcFrame: ElementRef;

  @Output() authenticate = new EventEmitter<AuthenticateEvent>();
  @Output() cartLoad = new EventEmitter<CartChangeEvent>();
  @Output() cartUpdate = new EventEmitter<CartChangeEvent>();
  @Output() continue = new EventEmitter<ContinueEvent>();
  @Output() navigation = new EventEmitter<NavigationEvent>();
  @Output() addOnError = new EventEmitter<AddOnErrorEvent>();

  readonly onDestroy = new Subject();
  showOIDCIframe: boolean;
  loading$: Observable<boolean>;

  private _context: string;
  private _mode: ComponentRenderMode = 'shop';
  private _enableCartSlideOut = true;
  private _enableAddressCollection = true;
  private _hideFooterNavigation = false;
  private _reloadEventListenerAttached = false;
  private _cartDeletedEventListenerAttached = false;
  private _isInitializing = false;
  private _initializeRunId = 0;
  private _destroyed = false;
  private _lastInitializedContext: string | undefined;

  //#region - services -
  private element: ElementRef;
  private router: Router;
  private navigationEventService: NavigationEventService;
  private store: Store;
  private initializationService: CommerceInitializationService;
  //#endregion - services -

  // This is a comment to trigger CommerceUI deploy
  constructor() {
    this.element = inject(ElementRef);
    this.router = inject(Router);
    this.navigationEventService = inject(NavigationEventService);
    this.store = inject(Store);
    this.initializationService = inject(CommerceInitializationService);

    this.store.dispatch(contextActions.resetState());
    this.loading$ = this.store.pipe(
      select(addonSelectors.selectApplicationLoading),
      debounceTime(25),
      takeUntil(this.onDestroy),
    );
  }

  get hideFooterNavigation(): boolean {
    return this._hideFooterNavigation;
  }

  @Input({transform: booleanAttribute}) set hideFooterNavigation(
    value: boolean,
  ) {
    this._hideFooterNavigation = value;
  }

  get mode(): ComponentRenderMode {
    return this._mode;
  }

  @Input() set mode(value: ComponentRenderMode) {
    this._mode = value;
    if (
      this._initializeRunId > 0 &&
      !this._isInitializing &&
      this.initializationService.initialized()
    ) {
      this.navigateOutlet();
    }
  }

  @Input({transform: booleanAttribute}) set enableAddressCollection(
    value: boolean,
  ) {
    this._enableAddressCollection = value;
    this.initializationService.enableAddressCollection = value;
  }

  @Input({transform: booleanAttribute}) set enableCartSlideOut(value: boolean) {
    this._enableCartSlideOut = value;
    this.initializationService.enableCartSlideOut = value;
  }

  @Input() set context(value: string) {
    this._context = value;
    this.initializationService.context = value;
  }

  ngOnInit(): void {
    this._destroyed = false;
    this.attachReloadEventListener();
    this.attachDeleteCartEventListener();
    void this.initializeAndNavigate();
  }

  /**
   * Theme base URL for SeFeThemesLoader. Production theme domain per SeFe docs;
   * can be overridden for staging/testing via env if needed.
   */
  private static readonly THEME_URL =
    'https://se-fe-angular-services.ui.sportngin.com';

  ngAfterViewInit(): void {
    this.implementJavaScriptInterface();
    this.loadTheme();
  }

  /**
   * Loads the SeFe theme layer into this component's shadow root so that
   * variables like --se-bg are defined and the store matches the intended look.
   * Uses the official Web Component install per SeFe docs: load directly into
   * shadowRoot so it works in external embeds (e.g. staging) regardless of
   * whether the host page includes the theme loader script.
   */
  private loadTheme(): void {
    const shadowRoot = this.element.nativeElement.shadowRoot;
    if (!shadowRoot) {
      return;
    }
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      shadowRoot,
      url: CommerceComponent.THEME_URL,
    }).catch(() => {
      // Theme load failure (e.g. network) is non-fatal; store still works, just unstyled
    });
  }

  attachReloadEventListener(): void {
    if (this._reloadEventListenerAttached) return;

    this._reloadEventListenerAttached = true;
    (<HTMLElement>this.element.nativeElement).addEventListener(
      'reload-commerce',
      () => {
        void this.initializeAndNavigate();
      },
    );
  }

  attachDeleteCartEventListener(): void {
    if (this._cartDeletedEventListenerAttached) return;

    this._cartDeletedEventListenerAttached = true;
    (<HTMLElement>this.element.nativeElement).addEventListener(
      'delete-cart',
      () => {
        this.store.dispatch(emptyCart());
      },
    );
  }

  implementJavaScriptInterface(): void {
    this.element.nativeElement.next = this.next.bind(this);
    this.element.nativeElement.back = this.back.bind(this);
    this.element.nativeElement.reload = this.reload.bind(this);
    this.element.nativeElement.deleteCart = this.deleteCart.bind(this);
  }

  reload(): void {
    void this.initializeAndNavigate();
  }

  private async initializeAndNavigate(): Promise<void> {
    if (this._isInitializing) {
      return;
    }

    this._isInitializing = true;
    const runId = ++this._initializeRunId;
    try {
      this.resetStateIfContextChanged();
      await this.initializationService.initialize(this);
      if (this._destroyed || runId !== this._initializeRunId) {
        return;
      }
      this._lastInitializedContext = this._context;
      this.navigateOutlet();
    } finally {
      this._isInitializing = false;
    }
  }

  private resetStateIfContextChanged(): void {
    if (this._context === this._lastInitializedContext) {
      return;
    }
    this.store.dispatch(contextActions.resetState());
    this.initializationService.enableCartSlideOut = this._enableCartSlideOut;
    this.initializationService.enableAddressCollection =
      this._enableAddressCollection;
    if (this._context) {
      this.initializationService.context = this._context;
    }
  }

  deleteCart(): void {
    this.store.dispatch(emptyCart());
  }

  navigateOutlet(): void {
    switch (this.mode) {
      case 'hidden':
      case 'checkout':
        this.router.navigate([{outlets: {commerceElement: this.mode}}]);
        break;
      case 'shop':
      default:
        this.router.navigate([{outlets: {commerceElement: 'entry'}}]);
        break;
    }
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._initializeRunId++;
    this.onDestroy.next(null);
  }

  /**
   * Method for integrator to navigate next
   */
  next(): void {
    this.navigationEventService.continueClick.emit();
  }

  /**
   * Method for integrator to navigate back
   */
  back(): void {
    this.navigationEventService.backClick.emit();
  }
}
